import { allowedQueryParamsForSyncing } from './allowedQueryParamsForSyncing';
import { QueryParam, QueryParams } from '../types';
import { featureFlagClient } from '@trello/feature-flag-client';
import { Analytics } from '@trello/atlassian-analytics';

interface AllowedQueryParamsForSyncing {
  [paramName: string]: Set<QueryParam>;
}

/**
 * Checks the queryParams provided to the request (from classic) and will
 * return true/false based on whether this query is 'safe to sync' to the
 * Apollo cache
 */
export const shouldSyncDeltaToCache = (
  queryParams: QueryParams,
  allowedQueryParams: AllowedQueryParamsForSyncing = allowedQueryParamsForSyncing,
): boolean => {
  const unsafeDeltaRejectionsEnabled = featureFlagClient.get(
    'fep.cache-syncing-reject-unsafe-deltas',
    false,
  );
  const unsafeDeltaRejectionMetricsEnabled = featureFlagClient.get(
    'fep.cache-syncing-reject-unsafe-deltas-metrics',
    false,
  );

  // If the feature flag is disabled, just always allow the delta (it's what we were doing
  // before)
  if (!unsafeDeltaRejectionsEnabled) {
    return true;
  }

  // Check the flat list of allowed query params to determine whether this payload should
  // be rejected
  for (const [paramName, paramValue] of Object.entries(queryParams)) {
    if (
      allowedQueryParams[paramName] &&
      !allowedQueryParams[paramName].has(paramValue)
    ) {
      if (unsafeDeltaRejectionMetricsEnabled) {
        Analytics.sendOperationalEvent({
          action: 'skipped',
          actionSubject: 'cacheSyncing',
          source: '@trello/graphql',
          attributes: {
            paramName,
            paramValue,
          },
        });
      }
      return false;
    }
  }

  if (unsafeDeltaRejectionMetricsEnabled) {
    Analytics.sendOperationalEvent({
      action: 'accepted',
      actionSubject: 'cacheSyncing',
      source: '@trello/graphql',
    });
  }
  return true;
};
